import React, { useEffect, useRef, useState } from 'react';
import type { Attachment } from '../types';
import { I } from './Shared';
import {
  filesToAttachments,
  formatBytes,
  imagePreviewUrl,
  MAX_ATTACHMENTS,
  ACCEPT_IMAGES,
  ACCEPT_DOCUMENTS,
  ACCEPT_ALL,
} from '../lib/attachments';

interface AttachmentBarProps {
  attachments: Attachment[];
  onChange: (next: Attachment[]) => void;
  disabled?: boolean;
  /** Compact rendering for the smaller step improve/answer boxes. */
  compact?: boolean;
  /** Overrides the default "Add" button label. */
  label?: string;
}

interface AddOption {
  key: string;
  label: string;
  icon: React.ReactNode;
  accept: string;
}

const ADD_OPTIONS: AddOption[] = [
  { key: 'images', label: 'Photos & images', icon: I.image, accept: ACCEPT_IMAGES },
  { key: 'docs', label: 'Documents & PDFs', icon: I.fileText, accept: ACCEPT_DOCUMENTS },
  { key: 'all', label: 'All supported files', icon: I.paperclip, accept: ACCEPT_ALL },
];

/**
 * Reusable attachment picker used by every prompt input (the Landing idea box
 * and the per-step improve/answer boxes). A "+" button opens a menu of add
 * options; each option opens the OS picker filtered to that file type. Only
 * supported types are accepted — unsupported files are rejected, not attached.
 * Text/PDF files are read + inlined into the prompt; images/other are stored
 * and previewed only.
 */
export function AttachmentBar({
  attachments,
  onChange,
  disabled = false,
  compact = false,
  label = '',
}: AttachmentBarProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const atCap = attachments.length >= MAX_ATTACHMENTS;

  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const addFiles = async (files: File[]): Promise<void> => {
    if (!files.length) return;
    setBusy(true);
    try {
      const existingBytes = attachments.reduce((sum, a) => sum + (a.size || 0), 0);
      const { attachments: added, errors: errs } = await filesToAttachments(
        files,
        attachments.length,
        existingBytes,
      );
      if (added.length) onChange([...attachments, ...added]);
      setErrors(errs);
    } finally {
      setBusy(false);
    }
  };

  const openPicker = (accept: string): void => {
    setMenuOpen(false);
    if (inputRef.current) {
      inputRef.current.accept = accept;
      inputRef.current.click();
    }
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    await addFiles(Array.from(e.target.files ?? []));
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (id: string): void => {
    onChange(attachments.filter((a) => a.id !== id));
    setErrors([]);
  };

  const iconFor = (a: Attachment): React.ReactNode => {
    const thumb = imagePreviewUrl(a);
    if (thumb) {
      return (
        <img
          src={thumb}
          alt={a.name}
          style={{ width: 20, height: 20, objectFit: 'cover', borderRadius: 3, flex: '0 0 auto' }}
        />
      );
    }
    return <span style={{ display: 'inline-flex', color: 'var(--ink-3)' }}>{I.fileText}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {attachments.map((a) => (
            <span
              key={a.id}
              className="chip"
              title={`${a.name} · ${formatBytes(a.size)}${
                a.kind === 'text'
                  ? ' · text inlined into prompt'
                  : a.kind === 'image'
                    ? ' · sent to the model as an image'
                    : ''
              }`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: 220,
                height: 26,
                paddingRight: 4,
              }}
            >
              {iconFor(a)}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                }}
              >
                {a.name}
              </span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${a.name}`}
                  onClick={() => remove(a.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: 'var(--ink-3)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'inline-flex', transform: 'scale(0.7)' }}>{I.x}</span>
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div ref={menuWrapRef} style={{ position: 'relative', flex: '0 0 auto' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={disabled || busy || atCap}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={compact ? 'Add attachment' : undefined}
            onClick={() => setMenuOpen((v) => !v)}
            title={atCap ? `Maximum ${MAX_ATTACHMENTS} attachments` : 'Add files, images, or documents'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ display: 'inline-flex', transform: 'scale(0.95)' }}>{I.plus}</span>
            {busy ? 'Reading…' : compact ? null : label}
          </button>

          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 6px)',
                left: 0,
                zIndex: 50,
                minWidth: 210,
                padding: 4,
                background: 'var(--surface)',
                border: '1px solid var(--line-strong, var(--line))',
                borderRadius: 8,
                boxShadow: 'var(--shadow-2, 0 8px 24px rgba(0,0,0,0.18))',
              }}
            >
              {ADD_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="menuitem"
                  onClick={() => openPicker(opt.accept)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 10px',
                    border: 0,
                    borderRadius: 6,
                    background: 'transparent',
                    color: 'var(--ink)',
                    fontSize: 13,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ display: 'inline-flex', color: 'var(--ink-3)' }}>{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {errors.length > 0 && (
          <span className="t-micro" style={{ color: 'var(--err)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {errors[0]}
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ALL}
          style={{ display: 'none' }}
          onChange={(e) => void onPick(e)}
        />
      </div>
    </div>
  );
}
